import { useI18n } from 'vue-i18n'

/* ── Types ── */
export type PlayerRole = 'goalie' | 'skater'
export type StatsBlock = { role: PlayerRole; data: any }
export type StatsCol = 'c3' | 'c4' | 'c5' | 'c6'

export interface StatsView {
  haveBoth: boolean
  primary: StatsBlock
  secondary: StatsBlock
}

/* ── Format helpers ── */
export function formatSv(value?: number | string | null): string {
  if (value === null || value === undefined) return '-'
  const num = Number(value)
  if (Number.isNaN(num)) return '-'
  const frac = num > 1.5 ? num / 100 : num
  const clamped = Math.min(Math.max(frac, 0), 0.999)
  return clamped.toFixed(3).slice(1)
}

export function round2(value?: number | string | null): string {
  if (value === null || value === undefined) return '-'
  const num = Number(value)
  return Number.isNaN(num) ? '-' : num.toFixed(2)
}

/* ── Parse raw API stats ── */
export function parseGoalie(t: any) {
  return {
    gp: t?.common?.gamesPlayed ?? 0,
    w: t?.common?.totalWins ?? 0,
    so: t?.goalie?.shootoutAttempts ?? 0,
    gaa: round2(t?.goalie?.goalsAgainstAverage),
    sv: formatSv(t?.goalie?.savePct),
  }
}

export function parseSkater(t: any) {
  return {
    gp: t?.common?.gamesPlayed ?? 0,
    g: t?.skater?.goals ?? 0,
    a: t?.skater?.assists ?? 0,
    p: t?.skater?.points ?? 0,
    pim: (t?.common?.penaltySeconds ?? 0) / 60,
  }
}

export const playedAsGoalie = (t: any) => (t?.common?.gamePlayedAsGoalie ?? 0) > 0
export const playedAsSkater = (t: any) => (t?.common?.gamePlayedAsSkater ?? 0) > 0

/* ── Build view for season / career ── */
export function buildStatsView(blocks: StatsBlock[]): StatsView {
  if (!blocks.length) {
    return {
      haveBoth: false,
      primary: { role: 'skater', data: { gp: 0, g: 0, a: 0, p: 0, pim: 0 } },
      secondary: { role: 'goalie', data: {} },
    }
  }
  if (blocks.length === 1) {
    return {
      haveBoth: false,
      primary: blocks[0],
      secondary: {
        role: blocks[0].role === 'goalie' ? 'skater' : 'goalie',
        data: {},
      },
    }
  }
  const [a, b] = blocks
  const primary = (a.data?.gp ?? 0) >= (b.data?.gp ?? 0) ? a : b
  const secondary = primary === a ? b : a
  return { haveBoth: true, primary, secondary }
}

/* ── Build blocks from raw API total ── */
export function buildBlocks(total: any): StatsBlock[] {
  const blocks: StatsBlock[] = []
  if (!total) return blocks
  if (playedAsGoalie(total)) blocks.push({ role: 'goalie', data: parseGoalie(total) })
  if (playedAsSkater(total)) blocks.push({ role: 'skater', data: parseSkater(total) })
  return blocks
}

/* ── Column label / key / value helpers ── */
export function keyFor(role: PlayerRole, col: StatsCol) {
  if (role === 'goalie') return { c3: 'w', c4: 'so', c5: 'gaa', c6: 'sv' }[col]
  return { c3: 'g', c4: 'a', c5: 'p', c6: 'pim' }[col]
}

export function valueWithFormat(role: PlayerRole, col: StatsCol, data: any) {
  const key = keyFor(role, col)
  const val = data?.[key]
  if (role === 'goalie' && col === 'c6') return formatSv(val)
  if (role === 'goalie' && col === 'c5') return round2(val)
  return val ?? '-'
}

/* ── Composable (provides t()-based labelFor) ── */
export function usePlayerStats() {
  const { t } = useI18n({ useScope: 'global' })

  function labelFor(role: PlayerRole, col: StatsCol) {
    if (role === 'goalie') return { c3: t('W'), c4: t('SO'), c5: t('GAA'), c6: t('SV%') }[col]
    return { c3: t('G'), c4: t('A'), c5: t('P'), c6: t('PIM') }[col]
  }

  return {
    labelFor,
    keyFor,
    valueWithFormat,
    formatSv,
    round2,
    parseGoalie,
    parseSkater,
    playedAsGoalie,
    playedAsSkater,
    buildStatsView,
    buildBlocks,
  }
}
